import { ChevronRight } from '@c/icons/chevron';
import BuyAddOfferButtons from '@c/product-details/BuyAddOfferButtons';
import { StatusBadgeNew } from '@c/dashboard/StatusBadgeNew';
import { useQuery } from '@tanstack/react-query';
import { getProductsByIds } from '@util/firestore/products';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import type { OfferDocument } from 'models/offer';
import { getOrderStatus, type OrderDocument } from 'models/order';
import type { ProductDocument } from 'models/product';
import type { UserDocument } from 'models/user';
import Link from 'next/link';
import { useState } from 'react';
import { motion } from 'framer-motion';
import SafeImage from '@ui/SafeImage';
import ProductPriceRange from '@ui/ProductPriceRange';
import { cn, isMobile } from '@util/index';
import OfferOperationModal from '@c/dashboard/OfferOperation/OfferOperationModal';
import MobileOfferOperationModal from '@c/dashboard/OfferOperation/MobileOfferOperationModal';
import { SoldOverlay } from '@c/dashboard/pages/ListingsPage';
import type { ChatDocument } from 'models/chat';

export type ChatPreviewSidebarProps = {
  product: ProductDocument | null | undefined;
  offer: OfferDocument | null | undefined;
  order: OrderDocument | null | undefined;
  userDoc: UserDocument | null | undefined;
  isFirstTimeLoading?: boolean;
  chat: ChatDocument;
  isLoading: boolean;
};

export default function ChatPreviewSidebar({
  product,
  offer,
  order,
  userDoc,
  isFirstTimeLoading = false,
  chat,
  isLoading,
}: ChatPreviewSidebarProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<string>('');

  const { data: products, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products', order?.product_ids],
    queryFn: () => getProductsByIds(order?.product_ids ?? []),
    enabled: !!order?.product_ids,
  });

  const isLoggedInAsSupport = userDoc?.roles?.support || userDoc?.roles?.admin;

  const orderStatusInfo = getOrderStatus(order, userDoc);

  // if logged in as support and the order has more than 1 product, don't show the order status
  const shouldShowOrderStatus = !(
    userDoc?.roles?.admin || userDoc?.roles?.support
  )
    ? true
    : (order?.product_ids?.length ?? 0) > 1
    ? false
    : true;

  if (!product && !offer && !order) return;

  const sidebarVariants = {
    hidden: {
      width: isFirstTimeLoading ? 0 : '40rem',
      opacity: isFirstTimeLoading ? 0 : 1,
    },
    visible: {
      width: '40rem',
      opacity: 1,
      transition: isFirstTimeLoading
        ? {
            type: 'spring',
            stiffness: 200,
            damping: 25,
            duration: 0.8,
            opacity: {
              delay: 0.3,
              duration: 0.5,
            },
          }
        : {
            duration: 0,
          },
    },
  };

  if (order) {
    const sellerProducts = products?.filter(
      (product) => product.seller_id === userDoc?.uid || userDoc?.roles?.support
    );

    return (
      <motion.div
        className="h-full w-[40rem] border-l border-zinc-200 bg-white p-8"
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        layout
      >
        <div className="flex w-full flex-col gap-6">
          {isLoading ? (
            <div className="flex w-full items-center gap-4">
              <div className="h-[2.1rem] w-48 animate-pulse rounded bg-zinc-200" />
              <div className="h-10 w-10 animate-pulse rounded bg-zinc-200" />
            </div>
          ) : (
            <Link
              href={`/dashboard/orders/${order.id}`}
              target="_blank"
              className="group flex w-full items-center gap-4"
            >
              <h3 className="text-[2.1rem] font-semibold text-zinc-700">
                Order #{order.order_num}
              </h3>

              <ChevronRight className="h-10 w-10 text-zinc-500 transition-all ease-linear group-hover:ml-2" />
            </Link>
          )}

          <div className="flex items-center gap-2 border-b border-zinc-200 pb-6">
            {isLoading || isLoadingProducts ? (
              <div className="flex flex-col gap-12">
                {/* Skeleton for 2 products */}
                {[1, 2].map((index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="aspect-square h-[10rem] w-[10rem] animate-pulse rounded-2xl bg-zinc-200" />
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-1">
                        <div className="h-[1.8rem] w-64 animate-pulse rounded bg-zinc-200" />
                        <div className="h-[1.8rem] w-48 animate-pulse rounded bg-zinc-200" />
                      </div>
                      <div className="h-[1.6rem] w-32 animate-pulse rounded bg-zinc-200" />
                      <div className="h-[2.4rem] w-40 animate-pulse rounded bg-zinc-200" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-12">
                {sellerProducts
                  ?.slice(0, isExpanded ? undefined : 2)
                  .map((product, index) => (
                    <div
                      key={product.id || index}
                      className="flex items-center gap-4"
                    >
                      {product.thumbnail ? (
                        <Link href={`/product/${product.slug}`} target="_blank">
                          <div className="aspect-square h-[10rem] w-[10rem] transition-opacity hover:opacity-80">
                            <SafeImage
                              src={product.thumbnail}
                              alt={product.title ?? ''}
                              width={400}
                              key={product.thumbnail}
                              height={400}
                              className="aspect-square h-full w-full rounded-2xl object-cover"
                            />
                          </div>
                        </Link>
                      ) : (
                        <div className="aspect-square h-[10rem] w-[10rem] bg-zinc-100" />
                      )}

                      <div className="flex flex-col gap-2">
                        <div className="flex flex-col gap-1">
                          <Link
                            href={`/product/${product.slug}`}
                            target="_blank"
                            className="group"
                          >
                            <h3 className="line-clamp-2 text-[1.8rem] font-medium text-zinc-700 transition-all ease-linear group-hover:text-zinc-900 group-hover:underline">
                              {product.title}
                            </h3>
                          </Link>

                          <ProductPriceRange product={product} vertical />
                        </div>

                        <ProductCategoryDetails product={product} />
                      </div>
                    </div>
                  ))}

                {sellerProducts && sellerProducts.length > 2 && (
                  <div className="flex w-full justify-center">
                    <button
                      type="button"
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="flex items-center gap-4 text-[1.8rem] font-medium text-zinc-500 transition-colors ease-linear hover:text-zinc-700"
                    >
                      {isExpanded
                        ? 'Show Less'
                        : `+${sellerProducts.length - 2} See More`}{' '}
                      <div className="h-8 w-8 rounded-full bg-brand-secondary p-1">
                        {isExpanded ? (
                          <ChevronUpIcon className="h-full w-full text-white" />
                        ) : (
                          <ChevronDownIcon className="h-full w-full text-white" />
                        )}
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-16">
            {isLoading ? (
              <div className="flex flex-col gap-2">
                <div className="h-[2.1rem] w-32 animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.8rem] w-24 animate-pulse rounded bg-zinc-200" />
              </div>
            ) : orderStatusInfo ? (
              <>
                {shouldShowOrderStatus && (
                  <StatusBadgeNew
                    status={orderStatusInfo.status}
                    is_partial={orderStatusInfo.is_partial}
                    variant={orderStatusInfo.variant}
                    date={orderStatusInfo.date}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col gap-2">
                <h4 className="text-[2.1rem] font-semibold text-zinc-700">
                  Order Status
                </h4>
                <span className="text-[1.8rem] font-semibold text-zinc-500">
                  Loading...
                </span>
              </div>
            )}

            {isLoading ? (
              <div className="duration-50 flex h-[5.5rem] animate-pulse rounded-[1.5rem] bg-zinc-200" />
            ) : (
              <Link
                href={`/dashboard/orders/${order.id}`}
                target="_blank"
                className="duration-50 text-zinc-black flex h-[5.5rem] items-center justify-center gap-4 rounded-[1.5rem] border border-zinc-900 text-[1.8rem] font-medium transition-colors ease-linear hover:bg-zinc-100"
              >
                View Order
              </Link>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  if (offer && product) {
    return (
      <motion.div
        className="h-full w-[40rem] border-l border-zinc-200 bg-white p-8"
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        layout
      >
        <div className="space-y-6">
          {isLoading ? (
            <div className="aspect-square w-full animate-pulse bg-zinc-200" />
          ) : (
            <Link href={`/product/${product.slug}`} target="_blank">
              <div className="relative transition-opacity hover:opacity-90">
                {product.thumbnail ? (
                  <SafeImage
                    src={product.thumbnail}
                    alt={product.title}
                    width={400}
                    key={product.thumbnail}
                    height={400}
                    className="aspect-square w-full object-cover"
                  />
                ) : (
                  <div className="aspect-square w-full bg-zinc-100" />
                )}

                {product.out_of_stock && <SoldOverlay variant="sold" />}
              </div>
            </Link>
          )}

          {isLoading ? (
            <div className="flex w-full items-center justify-between gap-6 border-b border-zinc-200 pb-6">
              <div className="flex flex-col items-start gap-2">
                <div className="h-[1.8rem] w-64 animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.8rem] w-48 animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.6rem] w-32 animate-pulse rounded bg-zinc-200" />
                <div className="h-[2.4rem] w-40 animate-pulse rounded bg-zinc-200" />
              </div>
              <div className="h-10 w-10 animate-pulse rounded-full bg-zinc-200" />
            </div>
          ) : (
            <Link
              className="group flex w-full items-center justify-between gap-6 border-b border-zinc-200 pb-6"
              href={`/product/${product.slug}`}
              target="_blank"
            >
              <div className="flex flex-col items-start gap-2">
                <h3 className="line-clamp-2 text-left text-[1.8rem] font-semibold text-zinc-700">
                  {product.title}
                </h3>

                <ProductPriceRange product={product} vertical />

                {!product.is_auction && (
                  <ProductCategoryDetails product={product} />
                )}
              </div>

              <ChevronRight className="duration-50 h-10 w-10 rounded-full p-1 text-zinc-500 transition-colors ease-linear group-hover:bg-zinc-100" />
            </Link>
          )}
        </div>

        {(chat.seller_id === userDoc?.uid || isLoggedInAsSupport) && (
          <Link
            className="duration-50 text-zinc-black mt-8 flex h-[5.5rem] items-center justify-center gap-4 rounded-[1.5rem] border border-zinc-900 text-[1.8rem] font-medium transition-colors ease-linear hover:bg-zinc-100"
            target="_blank"
            href={`/product/${product.slug}`}
          >
            View Listing
          </Link>
        )}

        {isMobile() ? (
          <>
            <MobileOfferOperationModal
              show={openModal === 'accept'}
              dismiss={() => setOpenModal('')}
              type="accept"
              offer={offer}
            />

            <MobileOfferOperationModal
              show={openModal === 'counter'}
              dismiss={() => setOpenModal('')}
              type="counter"
              offer={offer}
            />

            <MobileOfferOperationModal
              show={openModal === 'decline'}
              dismiss={() => setOpenModal('')}
              type="decline"
              offer={offer}
            />
          </>
        ) : (
          <>
            <OfferOperationModal
              isOpen={openModal === 'accept'}
              setIsOpen={(isOpen) => setOpenModal(isOpen ? 'accept' : '')}
              type="accept"
              onClose={() => setOpenModal('')}
              offer={offer}
            />

            <OfferOperationModal
              isOpen={openModal === 'counter'}
              setIsOpen={(isOpen) => setOpenModal(isOpen ? 'counter' : '')}
              type="counter"
              onClose={() => setOpenModal('')}
              offer={offer}
            />

            <OfferOperationModal
              isOpen={openModal === 'decline'}
              setIsOpen={(isOpen) => setOpenModal(isOpen ? 'decline' : '')}
              type="decline"
              onClose={() => setOpenModal('')}
              offer={offer}
            />
          </>
        )}
      </motion.div>
    );
  }

  if (product) {
    return (
      <motion.div
        className="h-full w-[40rem] border-l border-zinc-200 bg-white p-8"
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        layout
      >
        <div className="space-y-6">
          {isLoading ? (
            <div className="aspect-square w-full animate-pulse bg-zinc-200" />
          ) : (
            <Link href={`/product/${product.slug}`} target="_blank">
              <div className="relative transition-opacity hover:opacity-90">
                {product.thumbnail ? (
                  <SafeImage
                    src={product.thumbnail}
                    key={product.thumbnail}
                    alt={product.title}
                    width={400}
                    height={400}
                    className="aspect-square w-full object-cover"
                  />
                ) : (
                  <div className="aspect-square w-full bg-zinc-100" />
                )}

                {product.out_of_stock && <SoldOverlay variant="sold" />}
              </div>
            </Link>
          )}

          {isLoading ? (
            <div className="flex w-full items-center justify-between gap-6 border-b border-zinc-200 pb-6">
              <div className="flex flex-col items-start gap-2">
                <div className="h-[1.8rem] w-64 animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.8rem] w-48 animate-pulse rounded bg-zinc-200" />
                <div className="h-[1.6rem] w-32 animate-pulse rounded bg-zinc-200" />
                <div className="h-[2.4rem] w-40 animate-pulse rounded bg-zinc-200" />
              </div>
              <div className="h-10 w-10 animate-pulse rounded-full bg-zinc-200" />
            </div>
          ) : (
            <Link
              href={`/product/${product.slug}`}
              target="_blank"
              className="group flex w-full items-center justify-between gap-6 border-b border-zinc-200 pb-6"
            >
              <div className="flex flex-col items-start gap-2">
                <h3 className="line-clamp-2 text-left text-[1.8rem] font-semibold text-zinc-700">
                  {product.title}
                </h3>

                <ProductPriceRange product={product} vertical />

                {!product.is_auction && (
                  <ProductCategoryDetails product={product} />
                )}
              </div>

              <ChevronRight className="duration-50 h-10 w-10 rounded-full p-1 text-zinc-500 transition-colors ease-linear group-hover:bg-zinc-100" />
            </Link>
          )}

          {isLoading ? (
            <div className="flex flex-col gap-6">
              <div className="duration-50 flex h-[5.5rem] animate-pulse rounded-[1.5rem] bg-zinc-200" />
              <div className="flex flex-col gap-4">
                <div className="h-[5.5rem] w-full animate-pulse rounded-[1.5rem] bg-zinc-200" />
                <div className="h-[5.5rem] w-full animate-pulse rounded-[1.5rem] bg-zinc-200" />
              </div>
            </div>
          ) : (
            <>
              {(chat.seller_id === userDoc?.uid || isLoggedInAsSupport) && (
                <Link
                  className="duration-50 text-zinc-black flex h-[5.5rem] items-center justify-center gap-4 rounded-[1.5rem] border border-zinc-900 text-[1.8rem] font-medium transition-colors ease-linear hover:bg-zinc-100"
                  target="_blank"
                  href={`/product/${product.slug}`}
                >
                  View Listing
                </Link>
              )}

              {!product.out_of_stock && chat.buyer_id === userDoc?.uid && (
                <BuyAddOfferButtons
                  product={product}
                  seller={product.seller}
                  selectedVariations={null}
                  onAddToCartError={() => {}}
                />
              )}
            </>
          )}
        </div>
      </motion.div>
    );
  }
}

export const ProductCategoryDetails = ({
  product,
  className,
}: {
  product: ProductDocument;
  className?: string;
}) => {
  const hasSize = product.size && (product.size.letter || product.size.number);
  const hasVariations = !!product.variations?.length;

  if (!hasSize && !hasVariations) {
    return null;
  }

  return (
    <span
      className={cn(
        'line-clamp-1 h-[2.4rem] text-[1.8rem] font-medium text-zinc-500',
        className
      )}
    >
      {product.size?.letter && !product.has_variations
        ? `Size: ${product.size?.letter}`
        : ''}{' '}
      {product.size?.number && !product.has_variations
        ? `${product.size?.letter ? '/ ' : ''} Size: ${product.size?.number}`
        : ''}
      {hasVariations && (product.variations?.length ?? 0) > 1
        ? 'Multiple sizes'
        : ''}
    </span>
  );
};
